import { useState, useRef, useCallback, useLayoutEffect } from 'react';

/**
 * Util to get the current width of an element. Useful for when you need the width/height of a dynamic element at runtime.
 * @returns array of element size and ref setter callback for use similar to how you would use `useState`.
 * @see {@link useState}
 */
export function useElementSize(): [
  { width: number; height: number },
  (node: HTMLElement | null) => void
] {
  const nodeRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [elementSize, setSize] = useState({ width: 0, height: 0 });

  const setRef = useCallback((node: HTMLElement | null) => {
    // Disconnect any existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    nodeRef.current = node;
    if (!node) return;

    const observer = new ResizeObserver(entries => {
      if (!entries || entries.length === 0) return;
      const { width, height } = entries[0].contentRect;
      setSize({ width: Math.floor(width), height: Math.floor(height) });
    });

    observer.observe(node);
    observerRef.current = observer;
  }, []);

  // Ensure cleanup when the component unmounts
  useLayoutEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  return [elementSize, setRef];
}
